import { useMemo, useState } from 'react';
import { MessageCircle, Phone, Mail, Trash2, Building2 } from 'lucide-react';
import type { DemoRequestRow, DemoRequestStatus } from '@techsofit/api-types';
import { Card, Skeleton, EmptyState, cn } from '@techsofit/ui';
import { getDemoRequests, updateDemoRequest, deleteDemoRequest } from '../lib/adminApi';
import { useAsync } from '../lib/useAsync';

const STATUSES: { key: DemoRequestStatus; label: string; tone: string }[] = [
  { key: 'NEW', label: 'New', tone: 'bg-accent-soft text-ink' },
  { key: 'CONTACTED', label: 'Contacted', tone: 'bg-surface text-ink-soft' },
  { key: 'SCHEDULED', label: 'Scheduled', tone: 'bg-warn-soft text-warn-ink' },
  { key: 'WON', label: 'Won', tone: 'bg-ok-soft text-ok-ink' },
  { key: 'LOST', label: 'Lost', tone: 'bg-bad-soft text-bad-ink' },
];

/** Platform sales leads: demo requests submitted from the marketing site. */
export function LeadsPage() {
  const { data, loading, error, reload } = useAsync(getDemoRequests);
  const [filter, setFilter] = useState<DemoRequestStatus | 'ALL'>('ALL');

  const rows = useMemo(() => data ?? [], [data]);
  const counts = useMemo(() => {
    const c: Record<string, number> = { ALL: rows.length };
    for (const s of STATUSES) c[s.key] = 0;
    for (const r of rows) c[r.status] = (c[r.status] ?? 0) + 1;
    return c;
  }, [rows]);
  const shown = filter === 'ALL' ? rows : rows.filter((r) => r.status === filter);

  async function setStatus(r: DemoRequestRow, status: DemoRequestStatus) {
    await updateDemoRequest(r.id, { status }).catch(() => undefined);
    reload();
  }
  async function saveNotes(r: DemoRequestRow, notes: string) {
    if ((r.notes ?? '') === notes) return;
    await updateDemoRequest(r.id, { notes }).catch(() => undefined);
    reload();
  }
  async function remove(r: DemoRequestRow) {
    if (!window.confirm(`Delete the lead from ${r.name}?`)) return;
    await deleteDemoRequest(r.id).catch(() => undefined);
    reload();
  }

  return (
    <section className="animate-fade-up">
      <div className="mb-4">
        <h1 className="font-display text-2xl font-semibold text-ink">Leads</h1>
        <p className="text-sm text-muted">Demo requests submitted from the marketing site.</p>
      </div>

      <div className="mb-4 flex flex-wrap gap-1.5">
        <FilterChip label="All" count={counts.ALL} active={filter === 'ALL'} onClick={() => setFilter('ALL')} />
        {STATUSES.map((s) => (
          <FilterChip key={s.key} label={s.label} count={counts[s.key] ?? 0} active={filter === s.key} onClick={() => setFilter(s.key)} />
        ))}
      </div>

      {loading ? (
        <div className="flex flex-col gap-3">
          {Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-40" />)}
        </div>
      ) : error ? (
        <EmptyState title="Couldn’t load leads" description={error} />
      ) : shown.length === 0 ? (
        <EmptyState
          title={filter === 'ALL' ? 'No leads yet' : 'None in this stage'}
          description={filter === 'ALL' ? 'Demo requests from the marketing site will appear here.' : 'Try another filter.'}
          className="bg-surface"
        />
      ) : (
        <div className="flex flex-col gap-3">
          {shown.map((r) => (
            <LeadCard key={r.id} lead={r} onStatus={(s) => setStatus(r, s)} onSaveNotes={(n) => saveNotes(r, n)} onDelete={() => remove(r)} />
          ))}
        </div>
      )}
    </section>
  );
}

function FilterChip({ label, count, active, onClick }: { label: string; count: number; active: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'inline-flex items-center gap-1.5 rounded-pill px-3.5 py-1.5 text-sm font-medium transition-colors',
        active ? 'bg-ink text-on-dark' : 'bg-card text-ink-soft hover:bg-surface',
      )}
    >
      {label}
      <span className={cn('tabular text-xs', active ? 'text-on-dark-muted' : 'text-muted')}>{count}</span>
    </button>
  );
}

function LeadCard({
  lead,
  onStatus,
  onSaveNotes,
  onDelete,
}: {
  lead: DemoRequestRow;
  onStatus: (s: DemoRequestStatus) => void;
  onSaveNotes: (notes: string) => void;
  onDelete: () => void;
}) {
  const [notes, setNotes] = useState(lead.notes ?? '');
  const digits = (lead.phone ?? '').replace(/[^0-9]/g, '');
  const created = new Date(lead.createdAt).toLocaleString(undefined, { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });

  return (
    <Card className="flex flex-col gap-3 p-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="font-medium text-ink">{lead.name}</p>
          {lead.businessName && (
            <p className="flex items-center gap-1.5 text-sm text-muted">
              <Building2 className="size-3.5" aria-hidden /> {lead.businessName}
            </p>
          )}
        </div>
        <div className="flex items-center gap-2">
          <select
            value={lead.status}
            onChange={(e) => onStatus(e.target.value as DemoRequestStatus)}
            aria-label="Lead status"
            className="rounded-control border border-line bg-card px-3 py-1.5 text-sm text-ink"
          >
            {STATUSES.map((s) => (
              <option key={s.key} value={s.key}>{s.label}</option>
            ))}
          </select>
          <span className="text-xs text-muted tabular">{created}</span>
        </div>
      </div>

      {lead.message && <p className="rounded-control bg-surface px-3.5 py-2 text-sm text-ink-soft">{lead.message}</p>}

      <div className="flex flex-wrap items-center gap-2">
        {lead.email && (
          <a href={`mailto:${lead.email}`} className="inline-flex items-center gap-1.5 rounded-pill bg-surface px-3 py-1.5 text-sm text-ink-soft hover:text-ink">
            <Mail className="size-4" aria-hidden /> {lead.email}
          </a>
        )}
        {lead.phone && (
          <a href={`tel:${lead.phone}`} className="inline-flex items-center gap-1.5 rounded-pill bg-surface px-3 py-1.5 text-sm text-ink-soft hover:text-ink">
            <Phone className="size-4" aria-hidden /> {lead.phone}
          </a>
        )}
        {digits && (
          <a href={`https://wa.me/${digits}`} target="_blank" rel="noopener" className="inline-flex items-center gap-1.5 rounded-pill bg-ok-soft px-3 py-1.5 text-sm text-ok-ink">
            <MessageCircle className="size-4" aria-hidden /> WhatsApp
          </a>
        )}
        <span className="ml-auto rounded-pill bg-surface px-2.5 py-1 text-xs text-muted">via {lead.source.toLowerCase()}</span>
      </div>

      <textarea
        value={notes}
        onChange={(e) => setNotes(e.target.value)}
        onBlur={() => onSaveNotes(notes.trim())}
        rows={2}
        placeholder="Internal notes (saved when you click away)…"
        className="w-full rounded-control border border-line bg-elevated px-3 py-2 text-sm text-ink placeholder:text-muted focus:border-ink-soft"
      />

      <div className="flex justify-end">
        <button type="button" onClick={onDelete} className="inline-flex items-center gap-1.5 rounded-control px-2.5 py-1.5 text-sm font-medium text-muted hover:bg-bad-soft hover:text-bad-ink">
          <Trash2 className="size-4" aria-hidden /> Delete
        </button>
      </div>
    </Card>
  );
}
